/**
 * BUG-723 (reopened): where a newly-added product should land during a Return &
 * Exchange session — the Return cart (`cart`) or the Exchange cart (`exchangeCart`).
 *
 * This exact decision has regressed twice from the same root cause: four separate
 * call sites (cart.js, useProductOperations.js, useCartHandlers.js, Core/index.js)
 * each hand-rolled `selectedReturnType === "EXCHANGE" && !returnThisReceipt`, and
 * fixing the formula in one place while missing another is exactly how this broke
 * before (BUG-723's original fix, commit 879ff5b1, then BUG-1926's fix, commit
 * 9858549b — each changed the formula for one reported symptom without checking
 * every other call site shared the same behavior). One exported function, used
 * everywhere, replaces the duplicated formula so there is only one place to fix.
 *
 * Receipted flow (cart.isBlindReceipt falsy): unchanged from the pre-existing
 * behavior — `returnThisReceipt` decides, exactly as before this fix. This flow's
 * return items are loaded directly from the receipt (addSelectedReturnProductsToCart
 * writes straight to `cart` via setCart, bypassing this routing entirely), so
 * `returnThisReceipt` is never true here and every searched item correctly lands in
 * exchangeCart, same as always.
 *
 * Blind/receiptless flow (cart.isBlindReceipt === true, i.e. the "Return without
 * Receipt" / enableReceiptlessReturn2 path): there is no receipt to load return
 * items from, so `returnThisReceipt` is permanently false here by design (see
 * ReturnOptionsModal.js's comment) and can no longer distinguish anything. Instead,
 * this uses the one signal that's already there: the Return cart's own item count.
 * `cart` is created empty (isReturn/isBlindReceipt already set) the moment Continue
 * is confirmed, so the first product picked lands in the Return pane (recording what
 * the customer brought back, with batch-entry/negative-qty handling that's already
 * gated on cart.isReturn && cart.isBlindReceipt elsewhere), and every pick after that
 * — once there's at least one recorded return line — lands in the Exchange pane.
 */
export const computeIsExchangeTarget = ({ selectedReturnType, returnThisReceipt, cart }) => {
  if (selectedReturnType !== "EXCHANGE") return false;
  if (cart?.isBlindReceipt === true) return (cart?.items?.length || 0) > 0;
  return !returnThisReceipt;
};
